// WebSocketService.js — DROP-IN with Auto-Reconnect + Heartbeat
// Preserves your existing behavior and adds explicit fan-out for "resultDatesPF"
// API remains the same: connect(url, messageHandler?), subscribe(messageType, instrumentToken?, cb),
// unsubscribe(messageType, instrumentToken?, cb), send(msg), attachUnloadListener(), close()

const FRONTEND_BASE =
    process.env.REACT_APP_API_BASE ||
    window.location.origin; // fall back to current origin

class WebSocketService {
    static instance = null;
    ws = null;

    // existing fields
    messageHandler = null;
    subscribers = {};
    lastTickData = {}; // Store the last tick data for each GUIInstrument token

    // new fields for reconnect/heartbeat
    url = null;
    reconnectDelayMs = 1000;      // starts at 1s
    maxReconnectDelayMs = 30000;  // caps at 30s
    reconnectTimer = null;
    heartbeatTimer = null;
    heartbeatEveryMs = 25000;     // send a "ping" every 25s
    manualClose = false;          // if true, don't auto-reconnect
    _lifecycleListenersAttached = false;

    static getInstance() {
        if (!WebSocketService.instance) {
            WebSocketService.instance = new WebSocketService();
        }
        return WebSocketService.instance;
    }

    connect(url, messageHandler) {
        this.url = url;
        if (messageHandler) this.messageHandler = messageHandler;

        // avoid duplicate sockets
        if (this.ws && this.ws.readyState === WebSocket.OPEN) {
            console.log("WebSocket connection already exists.");
            return;
        }
        if (this.ws && this.ws.readyState === WebSocket.CONNECTING) {
            console.log("WebSocket is connecting");
            return;
        }

        this.manualClose = false;
        this._clearReconnectTimer();

        console.log("Creating a new WebSocket connection:", url);
        this.ws = new WebSocket(url);

        this.ws.onopen = () => {
            console.log("WebSocket connection opened");
            this._resetBackoff();
            this._startHeartbeat();
        };

        this.ws.onmessage = (event) => {
            let message;
            try {
                message = JSON.parse(event.data);
            } catch (e) {
                // allow non-JSON keepalives if server ever sends them
                // console.debug("Non-JSON WS frame:", event.data);
                return;
            }

            // >>> IGNORE SERVER PINGS <<<
            if (message && message.type === "ping") {
                // optionally console.debug("WS ping received");
                return;
            }

            if (this.messageHandler) {
                try { this.messageHandler(message); } catch (err) { console.error(err); }
            }
            this.notifySubscribers(message);

            // Persist last tick per token for late subscribers
            const token = message?.data?.instrumentToken;
            if (message?.type === "tickData" && token && message.data) {
                this.lastTickData[token] = message.data;
            }
        };

        this.ws.onerror = (error) => {
            console.error("WebSocket error:", error);
            // Let onclose handle the reconnect; proactively close noisy sockets
            try { this.ws.close(); } catch (_) {}
        };

        this.ws.onclose = () => {
            console.log("WebSocket connection closed");
            this._stopHeartbeat();
            this.ws = null;
            if (!this.manualClose) {
                this._scheduleReconnect();
            }
        };

        // attach once
        this._attachPageLifecycleListeners();
    }

    // ---------- Subscription API (preserved) ----------
    subscribe(messageType, instrumentToken, callback) {
        if (!this.subscribers[messageType]) this.subscribers[messageType] = {};

        if (instrumentToken) {
            if (!this.subscribers[messageType][instrumentToken]) {
                this.subscribers[messageType][instrumentToken] = [];
            }
            const arr = this.subscribers[messageType][instrumentToken];
            if (!arr.includes(callback)) {
                arr.push(callback);
                if (messageType === "tickData" && this.lastTickData[instrumentToken]) {
                    // Send the most recent tick immediately
                    try { callback({ type: "tickData", data: this.lastTickData[instrumentToken] }); } catch (e) { console.error(e); }
                }
            }
        } else {
            if (!this.subscribers[messageType].all) this.subscribers[messageType].all = [];
            const arr = this.subscribers[messageType].all;
            if (!arr.includes(callback)) arr.push(callback);
        }
    }

    unsubscribe(messageType, instrumentToken, callback) {
        const group = this.subscribers[messageType];
        if (!group) return;

        if (instrumentToken) {
            const arr = group[instrumentToken] || [];
            group[instrumentToken] = arr.filter((cb) => cb !== callback);
            if ((group[instrumentToken] || []).length === 0) delete group[instrumentToken];
        } else {
            const arr = group.all || [];
            group.all = arr.filter((cb) => cb !== callback);
            if ((group.all || []).length === 0) delete group.all;
        }

        if (Object.keys(group).length === 0) delete this.subscribers[messageType];
    }

    notifyAll(messageType, message) {
        const group = this.subscribers[messageType];
        if (!group) return;
        const arr = group.all || [];
        arr.forEach((cb) => { try { cb(message); } catch (e) { console.error(e); } });
    }

    notifySubscribers(message) {
        const { instrumentToken } = (message && message.data) || {};
        const messageType = (message && message.type) || "";

        switch (messageType) {
            case "tickData":
                // Persist handled in onmessage; now fan-out
                if (instrumentToken && this.subscribers.tickData && this.subscribers.tickData[instrumentToken]) {
                    this.subscribers.tickData[instrumentToken].forEach((cb) => { try { cb(message); } catch (e) { console.error(e); } });
                } else if (this.subscribers.tickData && this.subscribers.tickData.all) {
                    this.subscribers.tickData.all.forEach((cb) => { try { cb(message); } catch (e) { console.error(e); } });
                }
                break;

            case "bseAnnouncement":
                if (instrumentToken && this.subscribers.bseAnnouncement && this.subscribers.bseAnnouncement[instrumentToken]) {
                    this.subscribers.bseAnnouncement[instrumentToken].forEach((cb) => { try { cb(message); } catch (e) { console.error(e); } });
                } else if (this.subscribers.bseAnnouncement && this.subscribers.bseAnnouncement.all) {
                    this.subscribers.bseAnnouncement.all.forEach((cb) => { try { cb(message); } catch (e) { console.error(e); } });
                }
                break;

            case "nseAnnouncement":
                if (instrumentToken && this.subscribers.nseAnnouncement && this.subscribers.nseAnnouncement[instrumentToken]) {
                    this.subscribers.nseAnnouncement[instrumentToken].forEach((cb) => {
                        try { cb(message); } catch (e) { console.error(e); }
                    });
                } else if (this.subscribers.nseAnnouncement && this.subscribers.nseAnnouncement.all) {
                    this.subscribers.nseAnnouncement.all.forEach((cb) => {
                        try { cb(message); } catch (e) { console.error(e); }
                    });
                }
                break;

            // pluralized key used by the ScreenerAnnouncement context
            case "screenerAnnouncements":
                this.notifyAll("screenerAnnouncements", message);
                break;

            // important concalls live feed
            case "impConcalls":
                this.notifyAll("impConcalls", message);
                break;

            case "refreshPositions":
                this.notifyAll("refreshPositions", message);
                break;

            case "SSQ":
                if (instrumentToken && this.subscribers.SSQ && this.subscribers.SSQ[instrumentToken]) {
                    this.subscribers.SSQ[instrumentToken].forEach((cb) => { try { cb(message); } catch (e) { console.error(e); } });
                } else if (this.subscribers.SSQ && this.subscribers.SSQ.all) {
                    this.subscribers.SSQ.all.forEach((cb) => { try { cb(message); } catch (e) { console.error(e); } });
                }
                break;

            case "reloadInstruments":
                this.notifyAll("reloadInstruments", message);
                break;

            case "reloadSSG":
                this.notifyAll("reloadSSG", message);
                break;

            case "reloadStagG":
                this.notifyAll("reloadStagG", message);
                break;

            case "notification":
                this.notifyAll("notification", message);
                break;

            // broadcast "resultDates" to any subscriber (no instrumentToken filtering)
            case "resultDates":
                this.notifyAll("resultDates", message);
                break;

            // broadcast "resultDatesPF" (portfolio) to any subscriber
            case "resultDatesPF":
                this.notifyAll("resultDatesPF", message);
                break;

            case "redirect":
                if (this.ws) this.ws.close();
                window.location.replace(`${FRONTEND_BASE}/`);
                break;


            default:
                // Fallback: fan-out to every subscriber across message types
                Object.values(this.subscribers).forEach((typeSubscribers) => {
                    Object.values(typeSubscribers).forEach((callbacks) => {
                        (callbacks || []).forEach((cb) => {
                            try { cb(message); } catch (e) { console.error(e); }
                        });
                    });
                });
                break;
        }
    }

    send(message) {
        if (this.ws && this.ws.readyState === WebSocket.OPEN) {
            this.ws.send(JSON.stringify(message));
        } else {
            console.error("WebSocket is not open. ReadyState:", this.ws && this.ws.readyState);
        }
    }

    attachUnloadListener() {
        const handleUnload = () => {
            console.log("Closing WebSocket on window unload.");
            this.close();
        };
        window.addEventListener("beforeunload", handleUnload);
    }

    close() {
        this.manualClose = true;
        this._clearReconnectTimer();
        this._stopHeartbeat();
        if (this.ws) {
            try { this.ws.close(); } catch (_) {}
            this.ws = null;
        }
    }

    // ---------- Reconnect / Heartbeat helpers ----------
    _scheduleReconnect() {
        this._clearReconnectTimer();
        const jitter = Math.floor(Math.random() * 400); // +0..400ms jitter
        const delay = Math.min(this.reconnectDelayMs, this.maxReconnectDelayMs) + jitter;

        console.log(`Reconnecting in ${delay} ms`);
        this.reconnectTimer = setTimeout(() => {
            if (!this.url || this.manualClose) return;
            this.connect(this.url); // messageHandler preserved
            // backoff growth
            this.reconnectDelayMs = Math.min(this.reconnectDelayMs * 2, this.maxReconnectDelayMs);
        }, delay);
    }

    _resetBackoff() {
        this.reconnectDelayMs = 1000;
    }

    _clearReconnectTimer() {
        if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer);
            this.reconnectTimer = null;
        }
    }

    _startHeartbeat() {
        this._stopHeartbeat();
        // Send a light app-level ping periodically; server may ignore it — that's fine.
        this.heartbeatTimer = setInterval(() => {
            if (this.ws && this.ws.readyState === WebSocket.OPEN) {
                try { this.ws.send(JSON.stringify({ type: "ping", ts: Date.now() })); } catch (_) {}
            }
        }, this.heartbeatEveryMs);
    }

    _stopHeartbeat() {
        if (this.heartbeatTimer) {
            clearInterval(this.heartbeatTimer);
            this.heartbeatTimer = null;
        }
    }

    _attachPageLifecycleListeners() {
        if (this._lifecycleListenersAttached) return;
        this._lifecycleListenersAttached = true;

        window.addEventListener("online", () => {
            if (!this.ws && !this.manualClose && this.url) {
                this._resetBackoff();
                this._scheduleReconnect();
            }
        });

        document.addEventListener("visibilitychange", () => {
            if (document.visibilityState === "visible") {
                if (!this.ws && !this.manualClose && this.url) {
                    this._resetBackoff();
                    this._scheduleReconnect();
                }
            }
        });
    }
}

export default WebSocketService.getInstance();
